import React, { useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Button, Typography } from '@mui/material'

import Logo from '@/assets/AppLogo.png'
import GraphImage from '@/assets/LoginLightMode.svg'
import GraphImageDark from '@/assets/LoginDarkMode.svg'

import ThemeToggle from '@/components/sidebar/ThemeToggle'
import { useAppTheme } from '@/theme/ThemeContext'
import OtpCodeInput from '@/components/auth/OtpCodeInput'
import { useAuth } from '@/auth/AuthContext'
import { useSitesStore } from '@/store/sitesStore'
import { resolvePostAuthDestination } from '@/services/sitesService'
import { returnPath } from '@/auth/returnPath'

const OtpPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { mode, toggleTheme } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const { pendingEmail, verifyOtp, resendOtp, isLoading, error: backendError, isOtpStep, isAuthenticated, otpValidityDuration } = useAuth()
  const { setSelectedSite } = useSitesStore()

  const goAfterAuth = async () => {
    const back = returnPath(location.state)
    if (back) {
      navigate(back, { replace: true })
      return
    }
    const dest = await resolvePostAuthDestination()
    if (dest.site) setSelectedSite(dest.site)
    navigate(dest.path, { replace: true })
  }

  const [otp, setOtp] = useState<string>('')
  const [localError, setLocalError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const hasSubmittedRef = React.useRef(false)
  
  // Timer state
  const [timeLeft, setTimeLeft] = useState<number>(otpValidityDuration ?? 300)
  const [, setCanResend] = useState(false)
  const [isResending, setIsResending] = useState(false)

  const finalError = !isSubmitting ? (localError || backendError || null) : null

  // The Gateway sends the validity with the MFA_REQUIRED step
  useEffect(() => {
    if (otpValidityDuration) {
      setTimeLeft(otpValidityDuration)
      setCanResend(false)
    }
  }, [otpValidityDuration])

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true)
      return
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setCanResend(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [timeLeft])

  // Format time as MM:SS
  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }, [])

  useEffect(() => {
    if (!isOtpStep && !isAuthenticated) {
      navigate('/login', { replace: true })
    }
  }, [isOtpStep, isAuthenticated, navigate])

  useEffect(() => {
    const submitOtp = async () => {
      if (otp.length === 6 && !isLoading && !isSubmitting && !hasSubmittedRef.current) {
        hasSubmittedRef.current = true
        setIsSubmitting(true)
        setLocalError(null)
        
        const result = await verifyOtp(otp)

        if (result.success) {
          await goAfterAuth()
        } else if (result.error) {
          setLocalError('קוד אימות שגוי')
          hasSubmittedRef.current = false
        }
        setIsSubmitting(false)
      }
    }
    submitOtp()
  }, [otp])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (otp.length < 6) {
      setLocalError('נא להזין קוד מלא בן 6 ספרות')
      return
    }

    setLocalError(null)

    const result = await verifyOtp(otp)

    if (result.success) {
      await goAfterAuth()
    } else if (result.error) {
      setLocalError(result.error)
    }
  }

  const handleResend = async () => {
    if (isResending) return
    
    setIsResending(true)
    setLocalError(null)
    
    const result = await resendOtp()
    
    if (result.success) {
      // Reset timer
      setTimeLeft(otpValidityDuration ?? 300)
      setCanResend(false)
      setOtp('')
      hasSubmittedRef.current = false
    } else if (result.error) {
      setLocalError(result.error)
    }
    
    setIsResending(false)
  }

  const handleChangeEmail = () => {
    navigate('/login', { replace: true })
  }

  const colors = {
    background: isDarkMode ? '#0a1628' : '#F5FAFF',
    titleText: isDarkMode ? '#BBD7FF' : '#0B4F6C',
    welcomeText: isDarkMode ? '#7CAD44' : '#003A5D',
    subtitleText: isDarkMode ? '#94a3b8' : '#6B7A8C',
    languageText: isDarkMode ? '#94a3b8' : '#4A5C6A',
    buttonBg: isDarkMode ? '#1e3a5f' : '#E3F5FF',
    buttonText: isDarkMode ? '#FFFFFF' : '#1D3981',
    buttonHover: isDarkMode ? '#2d4a6f' : '#B3E0FF',
    changeLinkColor: isDarkMode ? '#94a3b8' : '#6B7A8C',
    timerColor: isDarkMode ? '#7CAD44' : '#0B4F6C',
  }

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        bgcolor: colors.background,
        transition: 'background-color 0.3s ease',
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          px: 8,
          py: 2,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 520,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            height: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="img"
              src={Logo}
              sx={{ width: '72px', height: '80px', objectFit: 'cover', objectPosition: 'top', borderRadius: '50%' }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: colors.titleText,
                transition: 'color 0.3s ease',
              }}
            >
              Rimonim Tech SmartNet
            </Typography>
          </Box>

          <Box
            component="img"
            src={isDarkMode ? GraphImageDark : GraphImage}
            alt="Water consumption graph"
            sx={{
              width: '80%',
              // borderRadius: 2,
              marginTop: '95px',
              marginLeft: '150px',
            }}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '18px',
              mt: 10,
              position: 'relative',
              right: '150px',
            }}
          >
            {[
              { label: 'פתוח', count: 3, color: '#8577E6', w: 584 },
              { label: 'בתהליך', count: 6, color: '#F79268', w: 484 },
              { label: 'בוצע', count: 32, color: '#46BE8B', w: 344 },
            ].map(card => (
              <Box
                key={card.label}
                sx={{
                  width: card.w,
                  height: 65,
                  backgroundColor: isDarkMode ? '#202B40' : '#FAFBFD',
                  borderRadius: '16px',
                  border: `1px solid ${isDarkMode ? '#374151' : 'rgba(50, 59, 119, 0.10)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  justifyContent: 'flex-end',
                  padding: '16px',
                  filter: 'drop-shadow(0px 8px 20px rgba(13, 75, 120, 0.08))',
                }}
              >
                <Typography sx={{ color: '#8F98A5', fontFamily: 'Rubik', fontSize: 16, fontWeight: 300 }}>
                  {card.count}
                </Typography>
                <Box
                  sx={{
                    backgroundColor: card.color,
                    color: '#EFF2F7',
                    fontFamily: 'Rubik',
                    fontSize: 20,
                    fontWeight: 400,
                    borderRadius: '4.5px',
                    padding: '10px 28px',
                    lineHeight: '24px',
                  }}
                >
                  {card.label}
                </Box>
                <Typography sx={{ color: isDarkMode ? '#FFFFFF' : '#003F61', fontSize: 12 }}>
                  ◄
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              mt: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: colors.languageText, fontWeight: 500, transition: 'color 0.3s ease' }}
            >
              HE
              <ThemeToggle
                isDarkMode={isDarkMode}
                onThemeToggle={toggleTheme}
              />
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          maxWidth: 600,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, md: 8 },
          py: 4,
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            width: '100%',
            maxWidth: 420,
            direction: 'rtl',
          }}
        >
          <Box sx={{ display: 'flex' }}>
            <Box
              component="img"
              src={Logo}
              alt="Rimonim Tech Logo"
              sx={{ width: '140px', height: '155px', objectFit: 'cover', objectPosition: 'top' }}
            />
          </Box>

          <Typography
            variant="h3"
            sx={{ fontWeight: 700, color: colors.welcomeText, textAlign: 'right', transition: 'color 0.3s ease' }}
          >
            ברוכים הבאים!
          </Typography>

          <Typography
            variant="body1"
            sx={{ mb: 5, color: colors.subtitleText, textAlign: 'right', transition: 'color 0.3s ease' }}
          >
           נשלח קוד אימות לכתובת: {pendingEmail || '...'}

            <br />
            נא להזין את הקוד בן 6 הספרות
          </Typography>

          {/* Timer and Resend - above OTP inputs */}
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
            }}
          >
            <Typography
              sx={{
                color: colors.subtitleText,
                fontSize: '1rem',
                fontWeight: 400,
              }}
            >
              {formatTime(timeLeft)}
            </Typography>
            
            <Typography
              component="span"
              onClick={!isResending ? handleResend : undefined}
              sx={{
                color: colors.timerColor,
                fontSize: '1rem',
                fontWeight: 400,
                textDecoration: 'underline',
                cursor: isResending ? 'default' : 'pointer',
                opacity: isResending ? 0.6 : 1,
                transition: 'color 0.3s ease, opacity 0.3s ease',
                '&:hover': {
                  color: !isResending ? colors.welcomeText : colors.timerColor,
                },
              }}
            >
              {isResending ? 'שולח...' : 'שליחה חוזרת'}
            </Typography>
          </Box>

          <OtpCodeInput
            value={otp}
            onChange={setOtp}
            length={6}
            hasError={Boolean(finalError)}
            isDarkMode={isDarkMode}
          />

          <Box
            sx={{
              mt: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '115px',
              marginRight: '20px',
            }}
          >
            <Box sx={{ flex: 1 }}>
              {finalError && (
                <Typography
                  variant="body2"
                  sx={{
                    color: '#E53935',
                    fontSize: '12px',
                    lineHeight: 1.4,
                    textAlign: 'right',
                  }}
                >
                  {finalError}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Button
                type="button"
                onClick={handleChangeEmail}
                sx={{
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  color: colors.changeLinkColor,
                  fontSize: '0.95rem',
                  transition: 'color 0.3s ease',
                }}
              >
                שינוי כתובת מייל
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                sx={{
                  borderRadius: 1,
                  py: 1,
                  px: 4,
                  minWidth: 160,
                  fontWeight: 400,
                  bgcolor: colors.buttonBg,
                  color: colors.buttonText,
                  boxShadow: '0px 1px 2px rgba(0, 130, 200, 0.25)',
                  whiteSpace: 'nowrap',
                  transition: 'background-color 0.3s ease, color 0.3s ease',
                  '&:hover': {
                    bgcolor: colors.buttonHover,
                  },
                }}
              >
                {isLoading ? 'מאמת...' : 'אימות וכניסה'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default OtpPage

